import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useToast } from '../components/Toast';

export type EmployeeStatus = 'active' | 'inactive' | 'on_leave' | 'terminated';

export interface Employee {
  id: string;
  employeeId: string;
  name?: string | null;
  email?: string | null;
  designation?: string | null;
  costCenter?: string | null;
  location?: string | null;
  employmentType?: string | null;
  departmentId?: string | null;
  managerEmployeeId?: string | null;
  status: EmployeeStatus;
  hireDate?: string | null;
  userId?: string | null;
  user?: { id: string; name: string; email: string; isActive: boolean; role: string } | null;
  department?: { id: string; name: string; code: string } | null;
  manager?: { id: string; employeeId: string; user?: { name: string } | null } | null;
}

export interface EmployeePayload {
  name: string;
  email?: string | null;
  userId?: string | null;
  employeeId?: string | null;
  designation?: string | null;
  employmentType?: string | null;
  location?: string | null;
  costCenter?: string | null;
  departmentId?: string | null;
  managerEmployeeId?: string | null;
  hireDate?: string | null;
  status?: EmployeeStatus;
}

export interface ImportResult { added: number; updated: number; skipped: number; total: number; errors: string[]; }

export interface SyncResult {
  added?: number; updated?: number; deactivated?: number; failed?: number;
  startedAt?: string; finishedAt?: string; triggeredBy?: string; error?: string;
  [k: string]: unknown;
}

export function useEmployees() {
  const { toast } = useToast();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const refetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/employees', { params: { search: search || undefined, limit: 100 } });
      setEmployees(res.data.employees ?? []);
      setTotal(res.data.total ?? 0);
    } catch (e: any) {
      toast(e?.response?.data?.error || 'Failed to load employees', 'error');
    } finally {
      setLoading(false);
    }
  }, [search, toast]);

  useEffect(() => { refetch(); }, [refetch]);

  const importCsv = async (file: File): Promise<ImportResult | null> => {
    const form = new FormData();
    form.append('file', file);
    try {
      const res = await api.post('/admin/employees/import', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast(`Imported: ${res.data.added} added, ${res.data.updated} updated, ${res.data.skipped} skipped`, 'success');
      await refetch();
      return res.data as ImportResult;
    } catch (e: any) {
      toast(e?.response?.data?.error || 'Import failed', 'error');
      return null;
    }
  };

  const normalizeDepartments = async () => {
    try {
      const res = await api.post('/admin/employees/normalize-departments');
      toast(`Matched ${res.data.matched} employees to departments`, 'success');
      await refetch();
    } catch (e: any) {
      toast(e?.response?.data?.error || 'Normalize failed', 'error');
    }
  };

  const saveEmployee = async (payload: EmployeePayload, id?: string): Promise<boolean> => {
    try {
      if (id) await api.put(`/admin/employees/${id}`, payload);
      else    await api.post('/admin/employees', payload);
      toast(id ? 'Employee updated' : 'Employee created', 'success');
      await refetch();
      return true;
    } catch (e: any) {
      const errs = e?.response?.data?.errors;
      toast(Array.isArray(errs) ? errs.join(', ') : (e?.response?.data?.error || 'Save failed'), 'error');
      return false;
    }
  };

  const syncM365 = async (): Promise<SyncResult | null> => {
    try {
      const res = await api.post('/admin/employees/sync-m365');
      const r: SyncResult = res.data.result ?? {};
      toast(`M365 sync complete: ${r.added ?? 0} added, ${r.updated ?? 0} updated, ${r.deactivated ?? 0} deactivated`, 'success');
      await refetch();
      return r;
    } catch (e: any) {
      toast(e?.response?.data?.error || 'M365 sync failed', 'error');
      return null;
    }
  };

  const getSyncStatus = async (): Promise<{ inProgress: boolean; lastResult: SyncResult | null } | null> => {
    try {
      const res = await api.get('/admin/employees/sync-status');
      return { inProgress: !!res.data.inProgress, lastResult: res.data.lastResult ?? null };
    } catch {
      return null;
    }
  };

  return {
    employees, total, loading, search, setSearch, refetch,
    importCsv, normalizeDepartments, saveEmployee, syncM365, getSyncStatus,
  };
}
